import { memo, type MouseEvent } from 'react';
import type { IconResult, InventoryItem } from '../../shared/types';
import { formatMoney, formatRelativeTime, itemStock, safeName, variationStock } from '../inventory';
import { ChevronIcon } from '../icons';

type Props = {
  item: InventoryItem;
  locationId: string | null;
  icon: IconResult | undefined;
  offline: boolean;
  onOpen: (item: InventoryItem) => void;
  onAdjust: (variationId: string, locationId: string | null, delta: number) => boolean;
};

export const ProductCard = memo(function ProductCard({ item, locationId, icon, offline, onOpen, onAdjust }: Props) {
  const stock = itemStock(item, locationId);
  const soleVariation = item.variations.length === 1 ? item.variations[0] : null;
  const price = soleVariation?.price ?? item.variations.find((variation) => variation.price)?.price ?? null;
  const sku = soleVariation?.sku || (item.variations.length > 1 ? `${item.variations.length} variations` : 'No SKU');
  const disabled = !locationId || offline;

  function open() { onOpen(item); }
  function openWithKeyboard(event: React.KeyboardEvent<HTMLElement>) { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); open(); } }
  function decrement(event: MouseEvent<HTMLButtonElement>) { event.stopPropagation(); if (soleVariation) onAdjust(soleVariation.id, locationId, -1); }
  function increment(event: MouseEvent<HTMLButtonElement>) { event.stopPropagation(); if (soleVariation) onAdjust(soleVariation.id, locationId, 1); }

  return (
    <article className={`product-card ${stock === 0 ? 'is-out' : stock <= 5 ? 'is-low' : ''}`} tabIndex={0} role="button" onClick={open} onKeyDown={openWithKeyboard} aria-label={`Open ${safeName(item.name)}, ${stock} in stock`}>
      <div className="product-icon" aria-hidden="true">{icon?.emoji ?? '🌿'}</div>
      <div className="product-copy">
        <h3>{safeName(item.name)}</h3>
        <p>{sku || 'No SKU'}{price ? ` · ${formatMoney(price.amount, price.currency)}` : ''}</p>
        <time dateTime={item.lastStockChange ?? undefined}>{formatRelativeTime(item.lastStockChange)}</time>
      </div>
      {soleVariation ? (
        <div className="quick-stock" aria-label={`Stock quantity ${variationStock(soleVariation, locationId)}`}>
          <button type="button" onClick={decrement} disabled={disabled || variationStock(soleVariation, locationId) === 0} aria-label={`Remove one ${safeName(item.name)}`}>−</button>
          <strong className={stock <= 5 ? 'low-number' : ''}>{stock.toLocaleString()}</strong>
          <button type="button" onClick={increment} disabled={disabled} aria-label={`Add one ${safeName(item.name)}`}>+</button>
        </div>
      ) : <div className="card-stock"><span>{stock.toLocaleString()}</span><small>in stock</small><ChevronIcon /></div>}
    </article>
  );
});
